import { animate, style, transition, trigger } from "@angular/animations";
import {
	AfterContentInit,
	AfterViewInit,
	Component,
	ContentChildren,
	Input,
	OnDestroy,
	Optional,
	QueryList,
	TemplateRef
} from "@angular/core";
import { AutomataTemplateDirective } from "@shared/directives/automata-template.directive";
import { Alert } from "@shared/schemas/alert";
import { filter, repeat, Subject, takeUntil, timer } from "rxjs";
import { AlertsService } from "./services/alerts.service";
import { AlertTemplate } from "./types";

@Component({
	selector: "app-alerts",
	templateUrl: "./alerts.component.html",
	styleUrls: ["./alerts.component.scss"],
	animations: [
		trigger("alertsAnimation", [
			transition(":enter", [
				style({ opacity: 0, transform: "translateY(-25%)" }),
				animate("300ms ease-out")
			]),
			transition(":leave", [
				animate(
					"300ms ease-out",
					style({
						opacity: 0,
						transform: "translateY(-100%)"
					})
				)
			])
		])
	],
	standalone: false
})
export class AlertsComponent
	implements AfterContentInit, AfterViewInit, OnDestroy
{
	@Input() key: string | undefined = undefined;
	@Input() severity: "info" | "success" | "warning" | "danger" = "info";
	@Input() values: Alert[] = [];
	@Input() dismissible = true;
	@Input() toastMode = false;
	@Input() timeToHideToast = 5000;
	@Input() stickyToast = false;

	@ContentChildren(AutomataTemplateDirective)
	private templates!: QueryList<AutomataTemplateDirective>;
	private toastLimit = 3;
	private _startToastTimer = new Subject<boolean>();
	private _stopToastTimer = new Subject<boolean>();
	private _onDestroy = new Subject<boolean>();

	protected alertBodyTemplate!: TemplateRef<unknown>;

	constructor(@Optional() private readonly alertsService: AlertsService) {}

	ngAfterContentInit(): void {
		this.prepareTemplates();
		this.watchForNewAlerts();
	}

	ngAfterViewInit(): void {
		this.initToastModeTimer();
	}

	ngOnDestroy(): void {
		this._onDestroy.next(true);
		this._stopToastTimer.next(true);
	}

	private removeAlertFromArray(): Alert | undefined {
		const removedAlert = this.values.pop();
		if (this.values.length === 0) {
			this.stopToastTimer();
		}
		return removedAlert;
	}

	private initToastModeTimer(): void {
		if (this.canActivateToastTimer()) {
			timer(this.timeToHideToast, this.timeToHideToast)
				.pipe(
					filter(() => this.values.length > 0),
					takeUntil(this._stopToastTimer),
					repeat({ delay: () => this._startToastTimer })
				)
				.subscribe(() => {
					this.removeAlertFromArray();
				});
		}
	}

	private prepareTemplates(): void {
		this.templates.forEach((item) => {
			const itemType = item.getType() as AlertTemplate;
			if (itemType === "alertBody") {
				this.alertBodyTemplate = item.template;
				return;
			}
			throw new Error("Tipo de template não definido");
		});
	}

	private resetToastTimer(): void {
		this.stopToastTimer();
		this.startToastTimer();
	}

	private shouldRestartToastTimer(): boolean {
		return this.values.length > 0;
	}

	private removeToastIfBiggerThanLimit(): Alert | undefined {
		if (this.values.length >= this.toastLimit) {
			this.resetToastTimer();
			return this.values.pop();
		}
		return undefined;
	}

	private watchForNewAlerts(): void {
		if (this.alertsService) {
			this.alertsService.alerts$
				.pipe(takeUntil(this._onDestroy))
				.subscribe((alerts) => {
					if (Array.isArray(alerts)) {
						if (this.key) {
							const filteredAlertsByKey = alerts.filter(
								(alert) => alert.key === this.key
							);
							this.values = filteredAlertsByKey;
							return;
						}
						this.values = alerts;
						return;
					}
					if (this.key === alerts.key) {
						this.removeToastIfBiggerThanLimit();
						this.values.unshift(alerts);
						if (this.shouldRestartToastTimer()) {
							this.resetToastTimer();
						}
					}
				});
		}
	}

	private canActivateToastTimer(): boolean {
		return this.toastMode && !this.stickyToast;
	}

	closeAlert(index: number): void {
		this.values.splice(index, 1);
		this.startToastTimer();
	}

	startToastTimer(): void {
		if (this.canActivateToastTimer()) {
			this._startToastTimer.next(true);
		}
	}

	stopToastTimer(): void {
		if (this.canActivateToastTimer()) {
			this._stopToastTimer.next(true);
		}
	}
}
